import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/utils/date-utils';
import {
  Package,
  Users,
  SatelliteDish,
  ChevronRight,
  Sparkles,
  BarChart3,
  Network,
  MapPin,
  Receipt,
  LucideIcon,
  Building2,
} from 'lucide-react';
import { ROUTES } from '@/config/routeConfig';
import { useMemo } from 'react';

interface SidebarProps {
  className?: string;
}

interface MenuSection {
  items: MenuItem[];
}

interface MenuItem {
  title: string;
  icon: LucideIcon;
  href: string;
  color: string;
  state?: unknown;
  badge?: string;
}

const Sidebar = ({ className }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Build menu sections based on user role
  const menuSections: MenuSection[] = useMemo(() => {
    return [
      {
        items: [
          {
            title: 'Accounts',
            icon: Building2,
            href: ROUTES.ACCOUNTS,
            color: 'text-emerald-500',
          },
          {
            title: 'Subscriptions',
            icon: Package,
            href: ROUTES.SUBSCRIPTIONS,
            color: 'text-green-500'
          },
          {
            title: 'Terminals',
            icon: SatelliteDish,
            href: ROUTES.TERMINALS,
            color: 'text-blue-500'
          },
          {
            title: 'Addresses',
            icon: MapPin,
            href: ROUTES.ADDRESSES,
            color: 'text-yellow-500'
          },
          {
            title: 'L2VPN Circuits',
            icon: Network,
            href: ROUTES.L2VPN_CIRCUITS,
            color: 'text-indigo-500'
          },
          {
            title: 'Datapool Consumption',
            icon: BarChart3,
            href: ROUTES.DATAPOOL_CONSUMPTION,
            color: 'text-cyan-500',
          },
          {
            title: 'Invoices',
            icon: Receipt,
            href: ROUTES.INVOICES,
            color: 'text-purple-500',
          },
          {
            title: 'Users',
            icon: Users,
            href: ROUTES.USERS,
            color: 'text-orange-500',
          }
        ]
      }
    ]
  }, []);

  const renderMenuItem = (item: MenuItem) => {
    const isActive = location.pathname === item.href;

    return (
      <div key={item.href} className="group">
        <Button
          variant={isActive ? "default" : "outline"}
          className={cn(
            "w-full justify-start gap-2 h-12 px-4",
          )}
          onClick={() => navigate(item.href, { state: item.state })}
        >
          <div className={cn("flex items-center justify-center w-8 h-8 rounded-lg transition-colors")}>
            <item.icon className="h-4 w-4" />
          </div>
          <div className="flex-1 text-left">
            <span>{item.title}</span>
          </div>
          {item.badge && (
            <div className="flex items-center">
              <span className="px-2 py-1 rounded-full">
                {item.badge}
              </span>
            </div>
          )}
          <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </Button>
      </div>
    );
  };

  const renderSection = (section: MenuSection, sectionKey: string) => {
    return (
      <div key={sectionKey} className="space-y-2">
        <div className="space-y-2">
          {section.items.map(item => renderMenuItem(item))}
        </div>
      </div>
    );
  };

  return (
    <div className={cn(
      'w-80 h-screen fixed flex-shrink-0',
      'glass-panel border-r',
      'shadow-xl shadow-black/5',
      className
    )}>
      {/* Navigation */}
      <div className="flex-1 p-4 overflow-y-auto">
        <nav className="space-y-2">
          {menuSections.map((section, index) =>
            renderSection(section, `section-${index}`)
          )}
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;